export type AmbienteFiscal = 'homologacao' | 'producao';

export type StatusNotaFiscal = 'rascunho' | 'processando' | 'autorizada' | 'erro' | 'cancelada' | 'denegada';

export type TipoPessoa = 'PF' | 'PJ';

export type IndicadorIe = 1 | 2 | 9;

export type CategoriaProduto = 'eletrico' | 'incendio' | 'outros';

export interface ConfigFiscal {
  id: number;
  ambiente: AmbienteFiscal;
  cnpj_emitente: string;
  natureza_operacao: string;
  aliquota_credito_simples: number | null;
  aliquota_tributos_aproximados: number;
  informacoes_complementares_padrao: string | null;
  updated_at: string;
}

export interface StatusIntegracaoFiscal {
  ambiente: AmbienteFiscal;
  /** No sistema publicado a emissão é sempre de produção, com valor fiscal. */
  producao_obrigatoria?: boolean;
  homologacao_configurado: boolean;
  producao_configurado: boolean;
  webhook_configurado: boolean;
  webhook_url: string | null;
}

export interface RegraFiscal {
  id: string;
  nome: string;
  descricao: string | null;
  cfop_interno: string;
  cfop_interestadual_contribuinte: string;
  cfop_interestadual_nao_contribuinte: string;
  csosn: string;
  pis_cst: string;
  cofins_cst: string;
  informacoes_complementares: string | null;
  padrao: boolean;
  ativo: boolean;
}

export interface ProdutoRevenda {
  id: string;
  codigo: string;
  descricao: string;
  categoria: CategoriaProduto;
  ncm: string;
  cest: string | null;
  origem: number;
  unidade: string;
  preco_venda: number;
  preco_custo: number;
  quantidade_estoque: number;
  regra_fiscal_id: string | null;
  ativo: boolean;
}

export interface DestinatarioNota {
  tipo_pessoa: TipoPessoa;
  nome: string;
  cpf?: string;
  cnpj?: string;
  indicador_ie: IndicadorIe;
  inscricao_estadual?: string;
  email?: string;
  telefone?: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  municipio: string;
  codigo_municipio?: string;
  uf: string;
}

export interface ItemNota {
  produto_id: string | null;
  codigo: string;
  descricao: string;
  ncm: string;
  cest: string | null;
  origem: number;
  unidade: string;
  quantidade: number;
  valor_unitario: number;
  valor_desconto: number;
  regra_fiscal_id: string | null;
  cfop?: string;
  csosn?: string;
}

export interface PagamentoNota {
  forma: string;
  indicador: 0 | 1;
  descricao?: string;
}

export interface CartaCorrecao {
  numero: number;
  correcao: string;
  criada_em: string;
  pdf_url: string | null;
  xml_url: string | null;
}

export interface NotaFiscal {
  id: string;
  ref: string;
  ambiente: AmbienteFiscal;
  status: StatusNotaFiscal;
  lead_id: string | null;
  conta_receber_id: string | null;
  destinatario: Partial<DestinatarioNota>;
  itens: ItemNota[];
  pagamento: Partial<PagamentoNota>;
  natureza_operacao: string | null;
  informacoes_complementares: string | null;
  modalidade_frete: number;
  consumidor_final: 0 | 1;
  presenca_comprador: number;
  valor_produtos: number;
  valor_desconto: number;
  valor_frete: number;
  valor_total: number;
  numero: string | null;
  serie: string | null;
  chave_nfe: string | null;
  protocolo: string | null;
  status_sefaz: string | null;
  mensagem_sefaz: string | null;
  erros: Array<{ mensagem?: string; campo?: string }> | null;
  danfe_url: string | null;
  xml_url: string | null;
  xml_cancelamento_url: string | null;
  cartas_correcao: CartaCorrecao[];
  justificativa_cancelamento: string | null;
  emitida_em: string | null;
  autorizada_em: string | null;
  cancelada_em: string | null;
  created_at: string;
}

export type NotaFiscalResumo = Pick<
  NotaFiscal,
  | 'id'
  | 'ref'
  | 'ambiente'
  | 'status'
  | 'numero'
  | 'serie'
  | 'chave_nfe'
  | 'destinatario'
  | 'valor_total'
  | 'mensagem_sefaz'
  | 'danfe_url'
  | 'xml_url'
  | 'conta_receber_id'
  | 'emitida_em'
  | 'autorizada_em'
  | 'cancelada_em'
  | 'created_at'
>;

export interface ClienteFiscal {
  id: string;
  nome: string | null;
  razao_social: string | null;
  tipo: string | null;
  cpf: string | null;
  cnpj: string | null;
  email: string | null;
  telefone: string | null;
  cep: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  codigo_municipio: string | null;
  inscricao_estadual: string | null;
  indicador_ie: IndicadorIe | null;
}

export interface ContaReceberOpcao {
  id: string;
  descricao: string;
  cliente: string | null;
  valor: number;
  data_vencimento: string;
  status: string;
}

export const STATUS_NOTA_LABEL: Record<StatusNotaFiscal, string> = {
  rascunho: 'Rascunho',
  processando: 'Processando',
  autorizada: 'Autorizada',
  erro: 'Rejeitada',
  cancelada: 'Cancelada',
  denegada: 'Denegada',
};

export const STATUS_NOTA_CLASSE: Record<StatusNotaFiscal, string> = {
  rascunho: 'bg-muted text-muted-foreground border-transparent',
  processando: 'bg-amber-100 text-amber-800 border-transparent dark:bg-amber-900/40 dark:text-amber-200',
  autorizada: 'bg-emerald-100 text-emerald-800 border-transparent dark:bg-emerald-900/40 dark:text-emerald-200',
  erro: 'bg-red-100 text-red-800 border-transparent dark:bg-red-900/40 dark:text-red-200',
  cancelada: 'bg-slate-200 text-slate-700 border-transparent dark:bg-slate-800 dark:text-slate-300',
  denegada: 'bg-red-200 text-red-900 border-transparent dark:bg-red-950 dark:text-red-200',
};

export const CATEGORIA_PRODUTO_LABEL: Record<CategoriaProduto, string> = {
  eletrico: 'Material elétrico',
  incendio: 'Prevenção de incêndio',
  outros: 'Outros',
};

export const INDICADOR_IE_LABEL: Record<IndicadorIe, string> = {
  1: 'Contribuinte de ICMS (tem IE)',
  2: 'Contribuinte isento de IE',
  9: 'Não contribuinte',
};

export const ORIGEM_MERCADORIA: Array<{ valor: number; label: string }> = [
  { valor: 0, label: '0 - Nacional' },
  { valor: 1, label: '1 - Estrangeira, importação direta' },
  { valor: 2, label: '2 - Estrangeira, adquirida no mercado interno' },
  { valor: 3, label: '3 - Nacional, conteúdo importado > 40%' },
  { valor: 4, label: '4 - Nacional, processo produtivo básico' },
  { valor: 5, label: '5 - Nacional, conteúdo importado até 40%' },
  { valor: 6, label: '6 - Estrangeira direta, sem similar nacional' },
  { valor: 7, label: '7 - Estrangeira mercado interno, sem similar nacional' },
  { valor: 8, label: '8 - Nacional, conteúdo importado > 70%' },
];

export const FORMAS_PAGAMENTO: Array<{ valor: string; label: string }> = [
  { valor: '17', label: 'PIX' },
  { valor: '15', label: 'Boleto bancário' },
  { valor: '03', label: 'Cartão de crédito' },
  { valor: '04', label: 'Cartão de débito' },
  { valor: '18', label: 'Transferência bancária' },
  { valor: '16', label: 'Depósito bancário' },
  { valor: '01', label: 'Dinheiro' },
  { valor: '02', label: 'Cheque' },
  { valor: '90', label: 'Sem pagamento' },
  { valor: '99', label: 'Outros' },
];

export const MODALIDADES_FRETE: Array<{ valor: number; label: string }> = [
  { valor: 9, label: 'Sem frete' },
  { valor: 0, label: 'Por conta do emitente (Sun7)' },
  { valor: 1, label: 'Por conta do destinatário' },
  { valor: 2, label: 'Por conta de terceiros' },
];

export const PRESENCA_COMPRADOR: Array<{ valor: number; label: string }> = [
  { valor: 1, label: 'Presencial' },
  { valor: 9, label: 'Não presencial (outros)' },
  { valor: 2, label: 'Internet' },
  { valor: 3, label: 'Teleatendimento' },
];

export const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
  'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];
